import { renderTextWithLineBreaks } from "@/helper/react-utils";
import { DescriptionItem, DescriptionList } from "components";
import { TimeSpanItem } from "components/time-span/time-span-item";
import { distanceUnit } from "constants/mapbox.const";
import { getRestrictionTypeName, moneyFormat, numberFormat } from "helper/format-data";
import { isEmpty, toolTipStyle } from "helper/utils";
import * as React from "react";
import { useMemo } from "react";
import { Restriction } from "types";
import { CurbZone } from "types/curb-zone.type";
import { CurbZoneHover, RestrictionHover } from "types/restriction.type";

type RestrictionTooltipProps = {
  restrictionHover: RestrictionHover | null;
  left: number;
  top: number;
  curbZoneHover?: CurbZoneHover | null;
};

export const RestrictionTooltip = (props: RestrictionTooltipProps) => {
  const { left, top, curbZoneHover, restrictionHover } = props;
  const ref = React.useRef<HTMLDivElement | null>(null);
  const styles = toolTipStyle({ top, left, height: 284, width: 288 });

  return (
    <div style={styles} className="absolute w-82" ref={ref}>
      {curbZoneHover && <CurbZoneInfo curbZone={curbZoneHover.properties} />}
      {restrictionHover && <RestrictionInfo restriction={restrictionHover.properties} />}
    </div>
  );
};

type CurbZoneInfoProps = {
  curbZone: CurbZone;
};
const CurbZoneInfo = ({ curbZone }: CurbZoneInfoProps) => {
  const firstPolicy = curbZone.curbPolicies ? curbZone?.curbPolicies[0] : null;

  let price = 0;
  if (!isEmpty(firstPolicy?.rules) && !isEmpty(firstPolicy?.rules?.[0]?.rate)) {
    price = firstPolicy?.rules?.[0]?.rate?.[0].rate || 0;
  }

  const policyNames = useMemo(() => {
    if (curbZone.curbPolicies) {
      const displayedName = curbZone.curbPolicies.reduce((names: string[], item) => {
        return [...names, item.name];
      }, []);
      return displayedName.join(", ");
    }
    return "";
  }, [curbZone.curbPolicies]);
  return (
    <div className="relative transition-opacity h-auto p-3 mt-2 mb-3 rounded-md shadow-lg z-10 bg-primary">
      <DescriptionList>
        <DescriptionItem label="Id" value={curbZone.curbZoneId} />
        <DescriptionItem label="Type de place" value={policyNames} />
        <DescriptionItem label="Forme" value={curbZone.parkingAngle} />
        <DescriptionItem label="Tarif" value={moneyFormat(price)} />
        <DescriptionItem label="Nom voie" value={curbZone?.streetName} />
        <DescriptionItem label="Longueur" value={`${numberFormat(curbZone.length)} ${distanceUnit}`} />
        <DescriptionItem label="Largeur" value={`${curbZone?.width} ${distanceUnit}`} />
        <DescriptionItem
          label="Surface calculée"
          value={`${numberFormat(curbZone?.calculatedArea)} ${distanceUnit}2`}
        />
        <DescriptionItem label="Places calculées" value={curbZone?.calculatedSpaces} />
        {curbZone?.externalId && <DescriptionItem label="Identifiant externe" value={curbZone?.externalId} />}
        {curbZone?.note && (
          <DescriptionItem label="Commentaire" value={<span>{renderTextWithLineBreaks(curbZone?.note)}</span>} />
        )}
      </DescriptionList>
    </div>
  );
};

type RestrictionInfoProps = {
  restriction: Restriction;
};

const RestrictionInfo = ({ restriction }: RestrictionInfoProps) => {
  const decreeFileNames = useMemo(() => {
    if (restriction?.decrees?.length) {
      return restriction?.decrees?.map((item) => item.name);
    }
    return [];
  }, [restriction]);

  return (
    <div className="relative transition-opacity h-auto p-3 mt-2 mb-3 rounded-md shadow-lg z-10 bg-primary">
      <DescriptionList>
        <DescriptionItem
          label="TimeSpans"
          value={
            <div className="flex flex-col">
              {restriction.timeSpans?.map((timeSpan, timeSpanIndex) => (
                <div className="flex items-center" key={`time-span-${timeSpanIndex}`}>
                  <TimeSpanItem
                    label=""
                    timeSpan={timeSpan}
                    name={`timeSpans[${timeSpanIndex}]`}
                    options={{
                      showSelectDate: true,
                    }}
                  />
                </div>
              ))}
            </div>
          }
        />
        <DescriptionItem label="Description" value={restriction?.description} />
        <DescriptionItem label="Decree" value={decreeFileNames.join(", ")} />
        {restriction.type && <DescriptionItem label="Type" value={getRestrictionTypeName(restriction.type)} />}
        {restriction.length && (
          <DescriptionItem label="Longueur" value={`${numberFormat(restriction.length)} ${distanceUnit}`} />
        )}
        {restriction.calculatedSpaces && (
          <DescriptionItem label="Places calculées" value={restriction.calculatedSpaces} />
        )}
      </DescriptionList>
    </div>
  );
};
